import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, FeatureGroup, Popup, Polygon } from 'react-leaflet';
import L from 'leaflet';
import { EditControl } from "react-leaflet-draw";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';

// Pastikan Anda mengimpor file CSS ini
import '../styles/GpsTracker.css'; 

// Daftarkan komponen Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_URL = "https://depotaircerdas.com/api/tracker";

const GpsTracker = () => {
    // === STATE UNTUK OTORISASI & DATA GPS ===
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loadingAuth, setLoadingAuth] = useState(true);

    const [lastPosition, setLastPosition] = useState(null);
    const [gpsTrail, setGpsTrail] = useState([]);
    const [deviceStatus, setDeviceStatus] = useState("Offline");
    const [lastSeen, setLastSeen] = useState("");
    const [isLiveTracking, setIsLiveTracking] = useState(true);
    const [historyData, setHistoryData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [totalDistance, setTotalDistance] = useState(0);
    const [avgSpeed, setAvgSpeed] = useState(0);
    const [totalDuration, setTotalDuration] = useState('0');
    const [isBarVisible, setIsBarVisible] = useState(true);
    const [geofences, setGeofences] = useState([]);
    const [currentDevice, setCurrentDevice] = useState(null);
    const [fuelEfficiency, setFuelEfficiency] = useState(10);

    // === REF UNTUK LEAFLET ===
    const drawnItemsRef = useRef(new L.FeatureGroup());
    const mapInstanceRef = useRef(null);
    
    // === LOGIKA AUTENTIKASI ===
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        const storedUsername = localStorage.getItem('username');
        const isAdmin = localStorage.getItem('is_super_admin') === 'true';

        if (storedToken && storedUsername) {
            setToken(storedToken);
            setUser(storedUsername);
            setCurrentDevice(storedUsername);
            setIsSuperAdmin(isAdmin);
        }
        setLoadingAuth(false);
    }, []);

    // === LOGIKA PELACAKAN (useEffect) ===
    useEffect(() => {
        if (!isLiveTracking || !token || !currentDevice) return;

        const fetchAndUpdate = () => {
            fetchLastPosition();
            fetchDeviceStatus();
        };

        fetchAndUpdate();
        const interval = setInterval(fetchAndUpdate, 5000);
        return () => clearInterval(interval);
    }, [isLiveTracking, token, currentDevice]);
    
    useEffect(() => {
        if (token && currentDevice) {
            fetchAndDisplayGeofence();
        }
    }, [token, currentDevice]);

    // === FUNGSI-FUNGSI PENGAMBIL DATA & LOGIKA ===
    const fetchLastPosition = async () => {
        try {
            const response = await fetch(`${API_URL}/last-position/${currentDevice}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (response.ok && result.success && result.data) {
                const { latitude, longitude, timestamp } = result.data;
                setLastPosition([latitude, longitude]);
                setLastSeen(new Date(timestamp).toLocaleString());
                setDeviceStatus('Online');
                setGpsTrail(prevTrail => {
                    const newPoint = [latitude, longitude];
                    const updatedTrail = [...prevTrail, newPoint].slice(-50);
                    return updatedTrail;
                });
            } else {
                setDeviceStatus('Offline');
                console.warn('Gagal mendapatkan posisi terakhir:', result.message);
            }
        } catch (error) {
            console.error("Gagal mengambil data GPS:", error);
            setDeviceStatus('Offline');
        }
    };
    
    const fetchDeviceStatus = async () => {
        try {
            const response = await fetch(`${API_URL}/device-status/${currentDevice}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (response.ok && result.success) {
                const { status, last_seen } = result.data;
                setDeviceStatus(status);
                setLastSeen(new Date(last_seen).toLocaleString());
            }
        } catch (error) {
            console.error("Gagal mengambil status perangkat:", error);
        }
    };

    const getHistory = async () => {
        setIsLiveTracking(false);
        if (!startDate || !endDate) {
            alert("Harap isi tanggal awal dan akhir.");
            return;
        }

        try {
            const historyResponse = await fetch(`${API_URL}/history/${currentDevice}?start_date=${startDate}&end_date=${endDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const summaryResponse = await fetch(`${API_URL}/summary/${currentDevice}?start_date=${startDate}&end_date=${endDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!historyResponse.ok || !summaryResponse.ok) {
                const errorHistory = await historyResponse.json();
                const errorSummary = await summaryResponse.json();
                throw new Error(`Gagal mengambil data: ${errorHistory.message || errorSummary.message}`);
            }

            const historyResult = await historyResponse.json();
            const summaryResult = await summaryResponse.json();

            if (historyResult.success && historyResult.data && summaryResult.success && summaryResult.data) {
                setHistoryData(historyResult.data);
                if (lastPosition) setLastPosition(null);
                setGpsTrail(historyResult.data.map(p => [p.latitude, p.longitude]));
                
                setTotalDistance(summaryResult.data.total_distance_km);
                setAvgSpeed(summaryResult.data.average_speed_kmh);
                setTotalDuration(formatDuration(summaryResult.data.total_duration_sec));
            } else {
                alert('Tidak ada riwayat perjalanan yang ditemukan untuk periode ini.');
                setHistoryData(null);
            }
        } catch (error) {
            console.error("Gagal mengambil riwayat perjalanan:", error);
            alert(`Terjadi kesalahan: ${error.message}`);
        }
    };
    
    const fetchAndDisplayGeofence = async () => {
        try {
            const response = await fetch(`${API_URL}/geofence/${currentDevice}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // ✅ Perbaikan utama: Tangani status 404 sebagai data kosong
            if (response.status === 404) {
                setGeofences([]); // Set geofence ke array kosong
                return;
            }

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data.length > 0) {
                    setGeofences(result.data.map(g => ({
                        ...g,
                        coordinates: JSON.parse(g.coordinates).map(c => L.latLng(c[0], c[1]))
                    })));
                } else {
                    setGeofences([]); // Set geofence ke array kosong jika data tidak ada
                }
            } else {
                // Tangani error lain selain 404
                console.error('Gagal mengambil geofence:', response.statusText);
                setGeofences([]);
            }
        } catch (error) {
            console.error('Gagal mengambil geofence:', error);
            setGeofences([]);
        }
    };
    
    const handleDrawCreated = (e) => {
        const { layer } = e;
        const geofenceName = prompt("Beri nama geofence ini:");
        if (geofenceName) {
            const coordinates = layer.getLatLngs()[0].map(latlng => [latlng.lat, latlng.lng]);
            saveGeofence(geofenceName, coordinates);
        } else {
            // Hapus layer yang baru digambar jika user tidak memberi nama
            e.target.removeLayer(layer);
        }
    };

    const saveGeofence = async (name, coordinates) => {
        try {
            const response = await fetch(`${API_URL}/geofence`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    username: currentDevice,
                    name: name,
                    coordinates: coordinates
                })
            });

            if (response.ok) {
                alert("Geofence berhasil disimpan!");
                fetchAndDisplayGeofence();
            } else {
                const error = await response.json();
                alert(`Gagal menyimpan geofence: ${error.message}`);
            }
        } catch (error) {
            console.error("Error saat menyimpan geofence:", error);
            alert("Terjadi kesalahan saat menyimpan geofence.");
        }
    };
    
    const handleDeviceChange = (e) => {
        setCurrentDevice(e.target.value);
        setIsLiveTracking(true);
        setGpsTrail([]);
        setHistoryData(null); 
    };

    const calculateFuel = () => {
        if (totalDistance > 0 && fuelEfficiency > 0) {
            return (totalDistance / fuelEfficiency).toFixed(2);
        }
        return '0';
    };

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${h} jam ${m} menit ${s} detik`;
    };
    
    const getChartData = () => {
        if (!historyData) return null;
        const labels = historyData.map(p => new Date(p.timestamp).toLocaleTimeString());
        const speeds = historyData.map(p => parseFloat(p.speed));
        return {
            labels,
            datasets: [{
                label: 'Kecepatan (km/h)',
                data: speeds,
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
                fill: false,
            }],
        };
    };

    const MapController = () => {
        const map = useMap();
        useEffect(() => {
            mapInstanceRef.current = map;
            if (lastPosition && isLiveTracking) {
                map.panTo(lastPosition);
            } else if (gpsTrail.length > 0 && !isLiveTracking) {
                const bounds = L.polyline(gpsTrail).getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds);
                }
            }
        }, [map, lastPosition, gpsTrail, isLiveTracking]);
        return null;
    };
    
    if (loadingAuth) {
        return <div style={{padding: '20px'}}>Memuat GPS Tracker...</div>;
    }

    if (!token) {
        return <div style={{padding: '20px'}}>Anda tidak memiliki akses. Silakan login.</div>;
    }

    return (
        <div className="gps-tracker-page-container">
            <MapContainer center={[-6.366, 106.880]} zoom={15}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapController />
                <FeatureGroup ref={drawnItemsRef}>
                    {isSuperAdmin && (
                         <EditControl
                            position='topright'
                            onCreated={handleDrawCreated}
                            draw={{
                                polygon: true,
                                polyline: false,
                                rectangle: false,
                                circle: false,
                                marker: false,
                                circlemarker: false
                            }}
                        />
                    )}
                </FeatureGroup>
                {lastPosition && (
                    <Marker position={lastPosition}>
                        <Popup>
                            Posisi Terakhir<br />{lastSeen}
                        </Popup>
                    </Marker>
                )}
                <Polyline positions={gpsTrail} color="red" />
                {geofences.map((g, index) => (
                    <Polygon key={index} positions={g.coordinates} pathOptions={{ color: 'blue' }}>
                        <Popup>{g.name}</Popup>
                    </Polygon>
                ))}
            </MapContainer>

            {/* Downbar */}
            <div className={`bottom-bar ${isBarVisible ? 'visible' : 'hidden'}`}>
                <div className="bottom-bar-header">
                    <h3>GPS Tracker</h3>
                    <button onClick={() => setIsBarVisible(!isBarVisible)}>
                        {isBarVisible ? '▼' : '▲'}
                    </button>
                </div>
                <div className="bottom-bar-content">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">Pilih Perangkat</h5>
                            <select id="deviceSelect" className="form-select" value={currentDevice} onChange={handleDeviceChange}>
                                <option value={user}>{user} (Perangkat Anda)</option>
                                {isSuperAdmin && (
                                    <>
                                        <option value="rumah_budi">rumah_budi</option>
                                        <option value="mobil_sales">mobil_sales</option>
                                    </>
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">Status Perangkat</h5>
                            <p className="card-text">Status: <span className={`badge bg-${deviceStatus === 'Online' ? 'success' : 'danger'}`}>{deviceStatus}</span></p>
                            <p className="card-text">Terakhir terlihat: <span id="lastSeen">{lastSeen}</span></p>
                        </div>
                    </div>
                    
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">Kontrol & Analisis</h5>
                            <div className="mb-3">
                                <label htmlFor="startDate" className="form-label">Dari Tanggal:</label>
                                <input type="date" id="startDate" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="endDate" className="form-label">Hingga Tanggal:</label>
                                <input type="date" id="endDate" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>
                            <div className="d-grid gap-2">
                                <button onClick={getHistory} className="btn btn-secondary">Tampilkan Riwayat</button>
                                <button onClick={() => { setIsLiveTracking(true); setHistoryData(null); }} className="btn btn-success">Pelacakan Langsung</button>
                            </div>
                        </div>
                    </div>

                    {historyData && (
                        <>
                            <div className="stats-panel card mt-3">
                                <div className="card-body">
                                    <h5 className="card-title">Statistik Perjalanan</h5>
                                    <p className="card-text">Total Jarak: <strong>{totalDistance}</strong> km</p>
                                    <p className="card-text">Durasi: <strong>{totalDuration}</strong></p>
                                    <p className="card-text">Kecepatan Rata-rata: <strong>{avgSpeed}</strong> km/h</p>
                                    <hr />
                                    <h5 className="card-title">Perkiraan BBM</h5>
                                    <p className="card-text">
                                        Efisiensi (km/L): 
                                        <input 
                                            type="number" 
                                            className="form-control d-inline-block w-auto" 
                                            value={fuelEfficiency}
                                            onChange={(e) => setFuelEfficiency(parseFloat(e.target.value))}
                                            min="1"
                                        />
                                    </p>
                                    <p className="card-text">Perkiraan BBM: <strong>{calculateFuel()}</strong> Liter</p>
                                </div>
                            </div>
                            <div className="chart-panel card mt-3">
                                <div className="card-body">
                                    <h5 className="card-title">Grafik Kecepatan</h5>
                                    {getChartData() && <Line data={getChartData()} />}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GpsTracker;